import React, { useMemo, useState } from 'react';
import { styled } from '@mui/system';
import { Check, ArrowLeft, Inbox, Mail } from 'lucide-react';
import {
  Card,
  Body,
  Muted,
  Avatar,
  OptionList,
  SecondaryButton,
  Pill,
  shuffledOrder,
  seedFrom
} from '../components/ui';
import { motion, fadeUp, popIn } from '../components/motion';
import { scoreQueue, QUAL } from '../engine/scoring';
import { L, t } from '../strings';

const URGENCY_COLOR = { 1: '#828282', 2: '#d5921b', 3: '#b21625' };

const TicketRow = styled('button')(({ theme, done, delay }) => ({
  position: 'relative',
  width: '100%',
  textAlign: 'left',
  display: 'flex',
  gap: 12,
  alignItems: 'center',
  padding: '12px 14px 12px 24px',
  marginBottom: 8,
  borderRadius: 12,
  border: `2px solid ${done ? theme.palette.grey[100] : '#e0e0e0'}`,
  backgroundColor: done ? theme.palette.grey[50] : '#fff',
  fontFamily: 'Rubik-Regular',
  cursor: 'pointer',
  transition:
    'border-color 160ms ease, transform 160ms ease, box-shadow 160ms ease, opacity 200ms ease',
  ...motion(`${fadeUp} 360ms ease-out ${delay}ms both`),
  '&:hover': done
    ? { borderColor: theme.palette.grey[200] }
    : {
        borderColor: theme.palette.primary.main,
        transform: 'translateY(-1px)',
        boxShadow: '0 6px 16px rgba(34, 34, 34, 0.06)'
      },
  '&:focus-visible': { outline: `3px solid ${theme.palette.primary.light}`, outlineOffset: 1 }
}));

const Unread = styled('span')(() => ({
  position: 'absolute',
  left: 9,
  top: '50%',
  width: 8,
  height: 8,
  marginTop: -4,
  borderRadius: '50%',
  backgroundColor: '#79c912'
}));

const Subject = styled('div')(({ read }) => ({
  fontFamily: 'Rubik-Regular',
  fontSize: 16,
  fontWeight: read ? 400 : 500,
  lineHeight: '22px',
  overflowWrap: 'anywhere'
}));

const InboxHead = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  flexWrap: 'wrap',
  padding: '10px 0 12px',
  marginBottom: 10,
  borderBottom: `1px solid ${theme.palette.grey[100]}`,
  fontFamily: 'Rubik-Regular',
  fontSize: 15,
  fontWeight: 500,
  '& > svg': { color: '#4f9a04' }
}));

const Count = styled('span')(({ theme }) => ({
  minWidth: 22,
  height: 22,
  padding: '0 7px',
  borderRadius: 100,
  backgroundColor: theme.palette.primary.salad,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 12,
  fontWeight: 500,
  boxSizing: 'border-box',
  ...motion(`${popIn} 300ms ease-out both`)
}));

const Progress = styled('div')(({ theme }) => ({
  marginLeft: 'auto',
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  fontSize: 13,
  fontWeight: 400,
  color: theme.palette.textTertiary.main
}));

const MiniBar = styled('span')(({ theme }) => ({
  width: 70,
  height: 6,
  borderRadius: 100,
  backgroundColor: theme.palette.grey[100],
  overflow: 'hidden',
  '& > span': {
    display: 'block',
    height: '100%',
    backgroundColor: '#79c912',
    borderRadius: 100,
    transition: 'width 500ms ease'
  }
}));

const Letter = styled('div')(({ theme }) => ({
  padding: '16px 18px',
  borderRadius: 12,
  backgroundColor: theme.palette.grey[50],
  marginBottom: 18,
  ...motion(`${fadeUp} 360ms ease-out 80ms both`)
}));
const From = styled('div')(({ theme }) => ({
  fontFamily: 'Rubik-Regular',
  fontSize: 13,
  color: theme.palette.textTertiary.main
}));

const sumFx = (list) => {
  const out = {};
  list.forEach((fx) => {
    Object.entries(fx || {}).forEach(([k, v]) => {
      out[k] = (out[k] || 0) + v;
    });
  });
  return out;
};

export default function QueueStep({ step, pack, answered, onSubmit, kpiLabels }) {
  const people = useMemo(
    () => Object.fromEntries(pack.people.map((p) => [p.key, p])),
    [pack.people]
  );
  const [openId, setOpenId] = useState(null);
  const [picks, setPicks] = useState({}); // ticketId -> option index
  const [openOrder, setOpenOrder] = useState([]);
  const tickets = step.tickets;
  // Once submitted, tickets reopen read-only with their feedback.
  const shownPicks = answered ? answered.picks : picks;

  const open = (id) => {
    setOpenId(id);
    if (!answered) setOpenOrder((o) => (o.includes(id) ? o : [...o, id]));
  };

  const pick = (ticket, idx) => {
    const next = { ...picks, [ticket.id]: idx };
    setPicks(next);
    if (tickets.every((tk) => next[tk.id] != null)) {
      const score = scoreQueue(
        tickets,
        next,
        openOrder.includes(ticket.id) ? openOrder : [...openOrder, ticket.id]
      );
      const later = tickets.flatMap((tk) =>
        tk.options[next[tk.id]].later ? [tk.options[next[tk.id]].later] : []
      );
      onSubmit({
        score,
        fx: sumFx(tickets.map((tk) => tk.options[next[tk.id]].fx)),
        later,
        picks: next,
        openOrder
      });
    }
  };

  const fromOf = (tk) => (typeof tk.from === 'string' ? people[tk.from] : null);
  const fromLabel = (tk) =>
    typeof tk.from === 'string'
      ? `${L(people[tk.from]?.name)} · ${L(people[tk.from]?.role)}`
      : L(tk.from);

  const current = tickets.find((tk) => tk.id === openId);
  if (current) {
    const order = shuffledOrder(current.options.length, seedFrom(pack.id + step.id + current.id));
    return (
      <Card key={current.id}>
        <SecondaryButton
          size="small"
          onClick={() => setOpenId(null)}
          startIcon={<ArrowLeft size={16} />}
          sx={{ marginBottom: '14px' }}>
          {t('backToInbox')}
        </SecondaryButton>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 14 }}>
          {fromOf(current) ? (
            <Avatar person={fromOf(current)} />
          ) : (
            <Avatar
              person={{ initials: L(current.from).slice(0, 2).toUpperCase(), color: '#525252' }}
            />
          )}
          <div>
            <Subject>{L(current.subject)}</Subject>
            <From>
              {fromLabel(current)} ·{' '}
              <span style={{ color: URGENCY_COLOR[current.urgency] }}>
                {t('urgency')[current.urgency - 1]}
              </span>{' '}
              · {t('sla')} {L(current.sla)}
            </From>
          </div>
        </div>
        <Letter>
          <Body>{L(current.body)}</Body>
        </Letter>
        <OptionList
          options={current.options}
          order={order}
          picked={shownPicks[current.id] ?? null}
          onPick={(i) => !answered && pick(current, i)}
          kpiLabels={kpiLabels}
        />
        {shownPicks[current.id] != null && (
          <SecondaryButton size="small" onClick={() => setOpenId(null)} sx={{ marginTop: '12px' }}>
            {t('backToInbox')}
          </SecondaryButton>
        )}
      </Card>
    );
  }

  const handled = tickets.filter((tk) => shownPicks[tk.id] != null).length;
  const unread = tickets.length - handled;
  return (
    <Card key="inbox">
      <Body style={{ marginBottom: 14 }}>{L(step.text)}</Body>
      <InboxHead>
        <Inbox size={18} />
        {t('inbox')}
        {unread > 0 && (
          <Count key={unread} title={t('unread', { n: unread })}>
            {unread}
          </Count>
        )}
        <Progress>
          {t('handledOf', { a: handled, n: tickets.length })}
          <MiniBar>
            <span style={{ width: `${(handled / tickets.length) * 100}%` }} />
          </MiniBar>
        </Progress>
      </InboxHead>
      {tickets.map((tk, i) => {
        const done = shownPicks[tk.id] != null;
        const q = done ? tk.options[shownPicks[tk.id]].q : null;
        return (
          <TicketRow
            key={tk.id}
            type="button"
            done={done}
            delay={i * 60}
            onClick={() => open(tk.id)}>
            {!done && <Unread />}
            {fromOf(tk) ? (
              <Avatar person={fromOf(tk)} size={36} />
            ) : (
              <Avatar
                person={{ initials: L(tk.from).slice(0, 2).toUpperCase(), color: '#525252' }}
                size={36}
              />
            )}
            <div style={{ flex: 1, minWidth: 0 }}>
              <Subject read={done}>{L(tk.subject)}</Subject>
              <From>
                <Mail size={12} style={{ verticalAlign: -1 }} /> {fromLabel(tk)} · {t('sla')}{' '}
                {L(tk.sla)}
              </From>
            </div>
            {done ? (
              <Pill color={q === 'best' ? '#4f9a04' : q === 'ok' ? '#d5921b' : '#b21625'}>
                <Check size={12} /> {t('handled')}
              </Pill>
            ) : (
              <Pill color={URGENCY_COLOR[tk.urgency]} style={{ color: URGENCY_COLOR[tk.urgency] }}>
                {t('urgency')[tk.urgency - 1]}
              </Pill>
            )}
          </TicketRow>
        );
      })}
      {answered && (
        <Muted style={{ marginTop: 10 }}>
          {t('score')}: {Math.round(answered.score * 100)}/100 ·{' '}
          {t('bestPicks', {
            a: tickets.filter((tk) => QUAL[tk.options[answered.picks[tk.id]].q] === 1).length,
            n: tickets.length
          })}
        </Muted>
      )}
    </Card>
  );
}
